import React, { useState, Component } from 'react';
import { CheckIcon, PlusIcon, SearchIcon, CalendarIcon, ClockIcon, FilterIcon, TrashIcon, EditIcon, FileTextIcon, PencilIcon, TagIcon, ChevronDownIcon, AlertCircleIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from './common/Button';
// Mock data for todos
const mockTodos = [{
  id: 'todo1',
  title: 'Schedule roof inspection',
  description: 'Contact ABC Roofing to schedule a detailed inspection of the property.',
  dueDate: '2023-06-10',
  priority: 'high',
  status: 'completed',
  project: 'Riverside Renovation',
  assignedTo: 'John Smith'
}, {
  id: 'todo2',
  title: 'Submit permit applications',
  description: 'Prepare and submit all necessary permit applications for the renovation project.',
  dueDate: '2023-06-15',
  priority: 'high',
  status: 'in-progress',
  project: 'Riverside Renovation',
  assignedTo: 'Maria Garcia'
}, {
  id: 'todo3',
  title: 'Order kitchen appliances',
  description: 'Research and order all kitchen appliances for the renovation.',
  dueDate: '2023-07-01',
  priority: 'medium',
  status: 'not-started',
  project: 'Riverside Renovation',
  assignedTo: 'John Smith'
}, {
  id: 'todo4',
  title: 'Finalize flooring selection',
  description: 'Choose between hardwood and engineered wood options for the living areas.',
  dueDate: '2023-06-20',
  priority: 'medium',
  status: 'in-progress',
  project: 'Riverside Renovation',
  assignedTo: 'Sarah Johnson'
}, {
  id: 'todo5',
  title: 'Contact utility companies',
  description: 'Set up accounts with electric, water, and gas providers for the property.',
  dueDate: '2023-06-25',
  priority: 'low',
  status: 'not-started',
  project: 'Riverside Renovation',
  assignedTo: 'Maria Garcia'
}, {
  id: 'todo6',
  title: 'Schedule final inspection',
  description: 'Coordinate with city inspectors for final approval of completed work.',
  dueDate: '2023-08-15',
  priority: 'high',
  status: 'not-started',
  project: 'Riverside Renovation',
  assignedTo: 'John Smith'
}];
// Mock data for team members
const mockTeamMembers = [{
  id: 'member1',
  name: 'John Smith'
}, {
  id: 'member2',
  name: 'Maria Garcia'
}, {
  id: 'member3',
  name: 'Sarah Johnson'
}, {
  id: 'member4',
  name: 'David Wong'
}];
// Mock data for projects
const mockProjects = [{
  id: 'proj1',
  name: 'Riverside Renovation'
}, {
  id: 'proj2',
  name: 'Office Building HVAC Upgrade'
}, {
  id: 'proj3',
  name: 'Warehouse Expansion'
}];
const TodoList = () => {
  const [todos, setTodos] = useState(mockTodos);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  // New todo template
  const newTodoTemplate = {
    id: `todo${Date.now()}`,
    title: '',
    description: '',
    dueDate: new Date().toISOString().split('T')[0],
    priority: 'medium',
    status: 'not-started',
    project: '',
    assignedTo: ''
  };
  // Filter todos based on search and filters
  const filteredTodos = todos.filter(todo => (filterPriority === 'all' || todo.priority === filterPriority) && (filterStatus === 'all' || todo.status === filterStatus) && (filterProject === 'all' || todo.project === filterProject) && (filterAssignee === 'all' || todo.assignedTo === filterAssignee) && (todo.title.toLowerCase().includes(searchTerm.toLowerCase()) || todo.description.toLowerCase().includes(searchTerm.toLowerCase())));
  // Sort todos by due date and priority
  const sortedTodos = [...filteredTodos].sort((a, b) => {
    // First by status (not-started first, then in-progress, then completed)
    const statusOrder = {
      'not-started': 0,
      'in-progress': 1,
      completed: 2
    };
    if (statusOrder[a.status] !== statusOrder[b.status]) {
      return statusOrder[a.status] - statusOrder[b.status];
    }
    // Then by due date (earliest first)
    const dateA = new Date(a.dueDate);
    const dateB = new Date(b.dueDate);
    if (dateA.getTime() !== dateB.getTime()) {
      return dateA.getTime() - dateB.getTime();
    }
    // Finally by priority (high first, then medium, then low)
    const priorityOrder = {
      high: 0,
      medium: 1,
      low: 2
    };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });
  const handleCreateTodo = () => {
    const newTodo = {
      ...newTodoTemplate
    };
    setSelectedTodo(newTodo);
    setIsEditing(true);
  };
  const handleSaveTodo = updatedTodo => {
    if (todos.find(todo => todo.id === updatedTodo.id)) {
      setTodos(todos.map(todo => todo.id === updatedTodo.id ? updatedTodo : todo));
    } else {
      setTodos([updatedTodo, ...todos]);
    }
    setSelectedTodo(updatedTodo);
    setIsEditing(false);
  };
  const handleDeleteTodo = todoId => {
    setTodos(todos.filter(todo => todo.id !== todoId));
    setSelectedTodo(null);
  };
  const handleStatusChange = (todoId, newStatus) => {
    setTodos(todos.map(todo => todo.id === todoId ? {
      ...todo,
      status: newStatus
    } : todo));
    if (selectedTodo && selectedTodo.id === todoId) {
      setSelectedTodo({
        ...selectedTodo,
        status: newStatus
      });
    }
  };
  const handleBackToList = () => {
    setSelectedTodo(null);
    setIsEditing(false);
  };
  // Get color for priority badge
  const getPriorityColor = priority => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get color for status badge
  const getStatusColor = status => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'not-started':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Format status for display
  const formatStatus = status => {
    switch (status) {
      case 'not-started':
        return 'Not Started';
      case 'in-progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      default:
        return status;
    }
  };
  return <div className="max-w-7xl mx-auto" data-id="element-2986">
      <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2987">To-Do List</h1>
      <div className="flex flex-col lg:flex-row gap-6" data-id="element-2988">
        {/* Main content area */}
        <div className="flex-1" data-id="element-2989">
          {selectedTodo ? isEditing ? <TodoEditor todo={selectedTodo} onSave={handleSaveTodo} onCancel={handleBackToList} projects={mockProjects} teamMembers={mockTeamMembers} data-id="element-2990" /> : <TodoViewer todo={selectedTodo} onEdit={() => setIsEditing(true)} onDelete={() => handleDeleteTodo(selectedTodo.id)} onStatusChange={newStatus => handleStatusChange(selectedTodo.id, newStatus)} onBack={handleBackToList} getPriorityColor={getPriorityColor} getStatusColor={getStatusColor} formatStatus={formatStatus} data-id="element-2991" /> : <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-2992">
              {/* Header with filters */}
              <div className="p-4 border-b border-gray-200" data-id="element-2993">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4" data-id="element-2994">
                  <div className="relative flex-grow max-w-md" data-id="element-2995">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2996">
                      <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-2997" />
                    </div>
                    <input type="text" className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Search tasks..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2998" />
                  </div>
                  <Button onClick={handleCreateTodo} data-id="element-2999">
                    <PlusIcon className="w-4 h-4 mr-2" data-id="element-3000" />
                    New Task
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-3001">
                  <div data-id="element-3002">
                    <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-3003">
                      Status
                    </label>
                    <select className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-id="element-3004">
                      <option value="all" data-id="element-3005">All Statuses</option>
                      <option value="not-started" data-id="element-3006">Not Started</option>
                      <option value="in-progress" data-id="element-3007">In Progress</option>
                      <option value="completed" data-id="element-3008">Completed</option>
                    </select>
                  </div>
                  <div data-id="element-3009">
                    <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-3010">
                      Priority
                    </label>
                    <select className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={filterPriority} onChange={e => setFilterPriority(e.target.value)} data-id="element-3011">
                      <option value="all" data-id="element-3012">All Priorities</option>
                      <option value="high" data-id="element-3013">High</option>
                      <option value="medium" data-id="element-3014">Medium</option>
                      <option value="low" data-id="element-3015">Low</option>
                    </select>
                  </div>
                  <div data-id="element-3016">
                    <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-3017">
                      Project
                    </label>
                    <select className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={filterProject} onChange={e => setFilterProject(e.target.value)} data-id="element-3018">
                      <option value="all" data-id="element-3019">All Projects</option>
                      {mockProjects.map(project => <option key={project.id} value={project.name} data-id="element-3020">
                          {project.name}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-3021">
                    <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-3022">
                      Assigned To
                    </label>
                    <select className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)} data-id="element-3023">
                      <option value="all" data-id="element-3024">All Team Members</option>
                      {mockTeamMembers.map(member => <option key={member.id} value={member.name} data-id="element-3025">
                          {member.name}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>
              {/* Todo list */}
              <div className="p-0" data-id="element-3026">
                {sortedTodos.length === 0 ? <div className="text-center py-12" data-id="element-3027">
                    <TagIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-3028" />
                    <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-3029">
                      No tasks found
                    </h3>
                    <p className="text-gray-500 mb-4" data-id="element-3030">
                      {searchTerm || filterPriority !== 'all' || filterStatus !== 'all' || filterProject !== 'all' || filterAssignee !== 'all' ? 'Try adjusting your filters or search term' : 'Create your first task to get started'}
                    </p>
                    <Button onClick={handleCreateTodo} data-id="element-3031">
                      <PlusIcon className="w-4 h-4 mr-2" data-id="element-3032" />
                      Add New Task
                    </Button>
                  </div> : <div className="divide-y divide-gray-200" data-id="element-3033">
                    {sortedTodos.map(todo => <div key={todo.id} className="flex items-center p-4 hover:bg-gray-50 cursor-pointer" onClick={() => {
                setSelectedTodo(todo);
                setIsEditing(false);
              }} data-id="element-3034">
                        <div className="mr-4" data-id="element-3035">
                          <button className={`w-6 h-6 rounded-full flex items-center justify-center border ${todo.status === 'completed' ? 'bg-green-100 border-green-500 text-green-500' : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'}`} onClick={e => {
                    e.stopPropagation();
                    handleStatusChange(todo.id, todo.status === 'completed' ? 'not-started' : 'completed');
                  }} data-id="element-3036">
                            {todo.status === 'completed' && <CheckIcon className="w-4 h-4" data-id="element-3037" />}
                          </button>
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-3038">
                          <div className="flex items-center" data-id="element-3039">
                            <p className={`font-medium ${todo.status === 'completed' ? 'text-gray-500 line-through' : 'text-gray-900'}`} data-id="element-3040">
                              {todo.title}
                            </p>
                            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(todo.priority)}`} data-id="element-3041">
                              {todo.priority.charAt(0).toUpperCase() + todo.priority.slice(1)}
                            </span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500 mt-1" data-id="element-3042">
                            <CalendarIcon className="w-3.5 h-3.5 mr-1" data-id="element-3043" />
                            <span data-id="element-3044">{todo.dueDate}</span>
                            {todo.project && <>
                                <span className="mx-2" data-id="element-3045">•</span>
                                <span data-id="element-3046">{todo.project}</span>
                              </>}
                            {todo.assignedTo && <>
                                <span className="mx-2" data-id="element-3047">•</span>
                                <span data-id="element-3048">{todo.assignedTo}</span>
                              </>}
                          </div>
                        </div>
                        <div className="ml-4" data-id="element-3049">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(todo.status)}`} data-id="element-3050">
                            {formatStatus(todo.status)}
                          </span>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </div>}
        </div>
        {/* Sidebar */}
        <div className="w-full lg:w-64 flex-shrink-0 order-first lg:order-last" data-id="element-3051">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4" data-id="element-3052">
            <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3053">
              Task Summary
            </h2>
            <div className="space-y-4" data-id="element-3054">
              <div data-id="element-3055">
                <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-3056">
                  By Status
                </h3>
                <div className="space-y-2" data-id="element-3057">
                  <div className="flex justify-between items-center" data-id="element-3058">
                    <div className="flex items-center" data-id="element-3059">
                      <div className="w-3 h-3 rounded-full bg-gray-300 mr-2" data-id="element-3060"></div>
                      <span className="text-sm text-gray-600" data-id="element-3061">Not Started</span>
                    </div>
                    <span className="text-sm font-medium" data-id="element-3062">
                      {todos.filter(t => t.status === 'not-started').length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-3063">
                    <div className="flex items-center" data-id="element-3064">
                      <div className="w-3 h-3 rounded-full bg-blue-400 mr-2" data-id="element-3065"></div>
                      <span className="text-sm text-gray-600" data-id="element-3066">In Progress</span>
                    </div>
                    <span className="text-sm font-medium" data-id="element-3067">
                      {todos.filter(t => t.status === 'in-progress').length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-3068">
                    <div className="flex items-center" data-id="element-3069">
                      <div className="w-3 h-3 rounded-full bg-green-400 mr-2" data-id="element-3070"></div>
                      <span className="text-sm text-gray-600" data-id="element-3071">Completed</span>
                    </div>
                    <span className="text-sm font-medium" data-id="element-3072">
                      {todos.filter(t => t.status === 'completed').length}
                    </span>
                  </div>
                </div>
              </div>
              <div data-id="element-3073">
                <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-3074">
                  By Priority
                </h3>
                <div className="space-y-2" data-id="element-3075">
                  <div className="flex justify-between items-center" data-id="element-3076">
                    <div className="flex items-center" data-id="element-3077">
                      <div className="w-3 h-3 rounded-full bg-red-400 mr-2" data-id="element-3078"></div>
                      <span className="text-sm text-gray-600" data-id="element-3079">High</span>
                    </div>
                    <span className="text-sm font-medium" data-id="element-3080">
                      {todos.filter(t => t.priority === 'high').length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-3081">
                    <div className="flex items-center" data-id="element-3082">
                      <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2" data-id="element-3083"></div>
                      <span className="text-sm text-gray-600" data-id="element-3084">Medium</span>
                    </div>
                    <span className="text-sm font-medium" data-id="element-3085">
                      {todos.filter(t => t.priority === 'medium').length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-3086">
                    <div className="flex items-center" data-id="element-3087">
                      <div className="w-3 h-3 rounded-full bg-green-400 mr-2" data-id="element-3088"></div>
                      <span className="text-sm text-gray-600" data-id="element-3089">Low</span>
                    </div>
                    <span className="text-sm font-medium" data-id="element-3090">
                      {todos.filter(t => t.priority === 'low').length}
                    </span>
                  </div>
                </div>
              </div>
              <div data-id="element-3091">
                <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-3092">
                  Due Soon
                </h3>
                <div className="space-y-2" data-id="element-3093">
                  {todos.filter(todo => {
                  const today = new Date();
                  const dueDate = new Date(todo.dueDate);
                  const diffTime = dueDate.getTime() - today.getTime();
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  return diffDays >= 0 && diffDays <= 7 && todo.status !== 'completed';
                }).slice(0, 3).map(todo => <div key={todo.id} className="p-2 bg-gray-50 rounded border border-gray-200 cursor-pointer hover:bg-gray-100" onClick={() => {
                  setSelectedTodo(todo);
                  setIsEditing(false);
                }} data-id="element-3094">
                        <div className="flex items-center" data-id="element-3095">
                          <AlertCircleIcon className="w-4 h-4 text-yellow-500 mr-2" data-id="element-3096" />
                          <p className="text-sm font-medium text-gray-700 truncate" data-id="element-3097">
                            {todo.title}
                          </p>
                        </div>
                        <div className="flex items-center mt-1 text-xs text-gray-500" data-id="element-3098">
                          <CalendarIcon className="w-3 h-3 mr-1" data-id="element-3099" />
                          <span data-id="element-3100">{todo.dueDate}</span>
                        </div>
                      </div>)}
                  {todos.filter(todo => {
                  const today = new Date();
                  const dueDate = new Date(todo.dueDate);
                  const diffTime = dueDate.getTime() - today.getTime();
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  return diffDays >= 0 && diffDays <= 7 && todo.status !== 'completed';
                }).length === 0 && <p className="text-sm text-gray-500 italic" data-id="element-3101">
                      No tasks due soon
                    </p>}
                </div>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-3102">
              <Link to="/file-cabinet" className="w-full flex items-center px-3 py-2 rounded-md text-left text-gray-700 hover:bg-gray-100" data-id="element-3103">
                <FileTextIcon className="w-5 h-5 mr-2 text-gray-400" data-id="element-3104" />
                <span data-id="element-3105">File Cabinet</span>
              </Link>
              <Link to="/notes" className="w-full flex items-center px-3 py-2 rounded-md text-left text-gray-700 hover:bg-gray-100" data-id="element-3106">
                <PencilIcon className="w-5 h-5 mr-2 text-gray-400" data-id="element-3107" />
                <span data-id="element-3108">Notes & Logs</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
// Todo Viewer Component
const TodoViewer = ({
  todo,
  onEdit,
  onDelete,
  onStatusChange,
  onBack,
  getPriorityColor,
  getStatusColor,
  formatStatus
}) => {
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-3109">
      <div className="p-4 border-b border-gray-200" data-id="element-3110">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-3111">
          <div className="flex items-center" data-id="element-3112">
            <button className="mr-3 text-gray-500 hover:text-gray-700" onClick={onBack} data-id="element-3113">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3114">
                <path d="M19 12H5M12 19l-7-7 7-7" data-id="element-3115" />
              </svg>
            </button>
            <h2 className="text-lg font-medium text-gray-900" data-id="element-3116">{todo.title}</h2>
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(todo.priority)}`} data-id="element-3117">
              {todo.priority.charAt(0).toUpperCase() + todo.priority.slice(1)}
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-3118">
            <Button size="sm" variant="outline" onClick={onEdit} data-id="element-3119">
              <EditIcon className="w-4 h-4 mr-1" data-id="element-3120" />
              Edit
            </Button>
            <Button size="sm" variant="danger" onClick={onDelete} data-id="element-3121">
              <TrashIcon className="w-4 h-4 mr-1" data-id="element-3122" />
              Delete
            </Button>
          </div>
        </div>
      </div>
      <div className="p-6" data-id="element-3123">
        <div className="flex flex-col md:flex-row gap-6" data-id="element-3124">
          <div className="flex-1" data-id="element-3125">
            <div className="mb-6" data-id="element-3126">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-3127">
                Description
              </h3>
              <p className="text-gray-700" data-id="element-3128">
                {todo.description || 'No description provided.'}
              </p>
            </div>
            <div className="mb-6" data-id="element-3129">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-3130">Status</h3>
              <div className="flex gap-2" data-id="element-3131">
                <button className={`px-3 py-1.5 rounded-md text-sm font-medium border ${todo.status === 'not-started' ? 'bg-gray-100 border-gray-300 text-gray-800' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`} onClick={() => onStatusChange('not-started')} data-id="element-3132">
                  Not Started
                </button>
                <button className={`px-3 py-1.5 rounded-md text-sm font-medium border ${todo.status === 'in-progress' ? 'bg-blue-100 border-blue-300 text-blue-800' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`} onClick={() => onStatusChange('in-progress')} data-id="element-3133">
                  In Progress
                </button>
                <button className={`px-3 py-1.5 rounded-md text-sm font-medium border ${todo.status === 'completed' ? 'bg-green-100 border-green-300 text-green-800' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`} onClick={() => onStatusChange('completed')} data-id="element-3134">
                  Completed
                </button>
              </div>
            </div>
          </div>
          <div className="md:w-64 flex-shrink-0" data-id="element-3135">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-3136">
              <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-3137">
                Details
              </h3>
              <div className="space-y-3" data-id="element-3138">
                <div data-id="element-3139">
                  <p className="text-xs text-gray-500" data-id="element-3140">Due Date</p>
                  <p className="font-medium flex items-center" data-id="element-3141">
                    <CalendarIcon className="w-3.5 h-3.5 mr-1 text-gray-400" data-id="element-3142" />
                    {todo.dueDate}
                  </p>
                </div>
                <div data-id="element-3143">
                  <p className="text-xs text-gray-500" data-id="element-3144">Priority</p>
                  <p className="font-medium" data-id="element-3145">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(todo.priority)}`} data-id="element-3146">
                      {todo.priority.charAt(0).toUpperCase() + todo.priority.slice(1)}
                    </span>
                  </p>
                </div>
                <div data-id="element-3147">
                  <p className="text-xs text-gray-500" data-id="element-3148">Status</p>
                  <p className="font-medium" data-id="element-3149">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(todo.status)}`} data-id="element-3150">
                      {formatStatus(todo.status)}
                    </span>
                  </p>
                </div>
                {todo.project && <div data-id="element-3151">
                    <p className="text-xs text-gray-500" data-id="element-3152">Project</p>
                    <p className="font-medium" data-id="element-3153">{todo.project}</p>
                  </div>}
                {todo.assignedTo && <div data-id="element-3154">
                    <p className="text-xs text-gray-500" data-id="element-3155">Assigned To</p>
                    <p className="font-medium" data-id="element-3156">{todo.assignedTo}</p>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
// Todo Editor Component
const TodoEditor = ({
  todo,
  onSave,
  onCancel,
  projects,
  teamMembers
}) => {
  const [editedTodo, setEditedTodo] = useState({
    ...todo
  });
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setEditedTodo({
      ...editedTodo,
      [name]: value
    });
  };
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-3157">
      <div className="p-4 border-b border-gray-200" data-id="element-3158">
        <div className="flex justify-between items-center" data-id="element-3159">
          <div className="flex items-center" data-id="element-3160">
            <button className="mr-3 text-gray-500 hover:text-gray-700" onClick={onCancel} data-id="element-3161">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3162">
                <path d="M19 12H5M12 19l-7-7 7-7" data-id="element-3163" />
              </svg>
            </button>
            <h2 className="text-lg font-medium text-gray-900" data-id="element-3164">
              {editedTodo.id.includes('todo') && !editedTodo.title ? 'New Task' : `Edit: ${editedTodo.title}`}
            </h2>
          </div>
          <div className="flex items-center gap-2" data-id="element-3165">
            <Button size="sm" variant="outline" onClick={onCancel} data-id="element-3166">
              Cancel
            </Button>
            <Button size="sm" onClick={() => onSave(editedTodo)} data-id="element-3167">
              Save Task
            </Button>
          </div>
        </div>
      </div>
      <div className="p-6" data-id="element-3168">
        <div className="space-y-4" data-id="element-3169">
          <div data-id="element-3170">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3171">
              Task Title
            </label>
            <input type="text" name="title" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editedTodo.title} onChange={handleInputChange} placeholder="Enter task title" data-id="element-3172" />
          </div>
          <div data-id="element-3173">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3174">
              Description
            </label>
            <textarea name="description" rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editedTodo.description} onChange={handleInputChange} placeholder="Enter task description" data-id="element-3175"></textarea>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3176">
            <div data-id="element-3177">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3178">
                Due Date
              </label>
              <input type="date" name="dueDate" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editedTodo.dueDate} onChange={handleInputChange} data-id="element-3179" />
            </div>
            <div data-id="element-3180">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3181">
                Priority
              </label>
              <select name="priority" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editedTodo.priority} onChange={handleInputChange} data-id="element-3182">
                <option value="high" data-id="element-3183">High</option>
                <option value="medium" data-id="element-3184">Medium</option>
                <option value="low" data-id="element-3185">Low</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3186">
            <div data-id="element-3187">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3188">
                Project
              </label>
              <select name="project" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editedTodo.project} onChange={handleInputChange} data-id="element-3189">
                <option value="" data-id="element-3190">Select a project</option>
                {projects.map(project => <option key={project.id} value={project.name} data-id="element-3191">
                    {project.name}
                  </option>)}
              </select>
            </div>
            <div data-id="element-3192">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3193">
                Assigned To
              </label>
              <select name="assignedTo" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={editedTodo.assignedTo} onChange={handleInputChange} data-id="element-3194">
                <option value="" data-id="element-3195">Assign to someone</option>
                {teamMembers.map(member => <option key={member.id} value={member.name} data-id="element-3196">
                    {member.name}
                  </option>)}
              </select>
            </div>
          </div>
          <div data-id="element-3197">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3198">
              Status
            </label>
            <div className="flex gap-2" data-id="element-3199">
              <button type="button" className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border ${editedTodo.status === 'not-started' ? 'bg-gray-100 border-gray-300 text-gray-800' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`} onClick={() => setEditedTodo({
              ...editedTodo,
              status: 'not-started'
            })} data-id="element-3200">
                Not Started
              </button>
              <button type="button" className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border ${editedTodo.status === 'in-progress' ? 'bg-blue-100 border-blue-300 text-blue-800' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`} onClick={() => setEditedTodo({
              ...editedTodo,
              status: 'in-progress'
            })} data-id="element-3201">
                In Progress
              </button>
              <button type="button" className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border ${editedTodo.status === 'completed' ? 'bg-green-100 border-green-300 text-green-800' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`} onClick={() => setEditedTodo({
              ...editedTodo,
              status: 'completed'
            })} data-id="element-3202">
                Completed
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default TodoList;